import { getApiBase, getApiKey } from "./client";
import type { GpsPosition } from "./types";

export type GpsMessage =
  | { type: "snapshot"; positions: GpsPosition[] }
  | { type: "update"; position: GpsPosition };

export type Subscribe = { kind: "all" } | { kind: "train"; train_id: number };

export type ValidGps = GpsPosition & { latitude: number; longitude: number };

export function isValidGps(pos: GpsPosition | null | undefined): pos is ValidGps {
  if (!pos) return false;
  const { latitude, longitude } = pos;
  if (typeof latitude !== "number" || typeof longitude !== "number") return false;
  if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) return false;
  if (latitude === 0 && longitude === 0) return false;
  return true;
}

export function wsUrl(): string {
  const u = new URL("/v1/ws", getApiBase());
  u.protocol = u.protocol === "https:" ? "wss:" : "ws:";
  u.searchParams.set("api_key", getApiKey());
  return u.toString();
}

type StatusListener = (connected: boolean) => void;

let socket: WebSocket | null = null;
let currentSub: Subscribe | null = null;
let listeners = new Set<(msg: GpsMessage) => void>();
let statusListeners = new Set<StatusListener>();
let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
let attempt = 0;
let wantOpen = false;

function notifyStatus(connected: boolean) {
  for (const fn of statusListeners) fn(connected);
}

function send(obj: unknown) {
  if (socket?.readyState === WebSocket.OPEN) {
    socket.send(JSON.stringify(obj));
  }
}

function applySubscribe() {
  if (!currentSub) return;
  if (currentSub.kind === "all") {
    send({ action: "subscribe", all: true });
  } else {
    send({ action: "subscribe", train_id: currentSub.train_id });
  }
}

function applyUnsubscribe(sub: Subscribe | null) {
  if (!sub) return;
  if (sub.kind === "all") {
    send({ action: "unsubscribe", all: true });
  } else {
    send({ action: "unsubscribe", train_id: sub.train_id });
  }
}

function open() {
  if (socket && (socket.readyState === WebSocket.OPEN || socket.readyState === WebSocket.CONNECTING)) {
    return;
  }

  const ws = new WebSocket(wsUrl());
  socket = ws;

  ws.onopen = () => {
    attempt = 0;
    notifyStatus(true);
    applySubscribe();
  };

  ws.onmessage = (ev) => {
    try {
      const msg = JSON.parse(String(ev.data)) as GpsMessage;
      if (msg.type === "snapshot" || msg.type === "update") {
        for (const fn of listeners) fn(msg);
      }
    } catch {
      // ignore non-JSON frames
    }
  };

  ws.onerror = () => {
    // onclose handles reconnect
  };

  ws.onclose = () => {
    socket = null;
    notifyStatus(false);
    if (!wantOpen) return;
    const delay = Math.min(1000 * 2 ** attempt, 15000);
    attempt += 1;
    reconnectTimer = setTimeout(open, delay);
  };
}

export function subscribeGps(sub: Subscribe, onMessage: (msg: GpsMessage) => void, onStatus?: StatusListener) {
  listeners.add(onMessage);
  if (onStatus) statusListeners.add(onStatus);
  currentSub = sub;
  wantOpen = true;

  if (!socket || socket.readyState !== WebSocket.OPEN) {
    open();
  } else {
    applySubscribe();
    onStatus?.(true);
  }

  return () => {
    listeners.delete(onMessage);
    if (onStatus) statusListeners.delete(onStatus);
    applyUnsubscribe(sub);
    if (currentSub === sub || (currentSub?.kind === sub.kind && currentSub.kind === "train" && sub.kind === "train" && currentSub.train_id === sub.train_id) || (currentSub?.kind === "all" && sub.kind === "all")) {
      currentSub = null;
    }
    if (listeners.size === 0) {
      wantOpen = false;
      if (reconnectTimer) {
        clearTimeout(reconnectTimer);
        reconnectTimer = null;
      }
      socket?.close();
      socket = null;
      notifyStatus(false);
    }
  };
}
